/**
 * PublicOfferPage
 *
 * Anonymous public offer view rendered at /public/offers/:token.
 * Fetches through the ApiClient plug's `callPublic` (no JWT, no session) using the public token.
 *
 * MUST NOT read the current user — this page has no authenticated context.
 * MUST NOT render inside AppShell — the root route handles AppShell skipping.
 *
 * Error routing:
 *   - 410 (PUBLIC_LINK_EXPIRED) → PublicOfferExpiredPage
 *   - 404 (invalid / revoked token) → PublicOfferInvalidPage
 *   - other → generic error
 *
 * Traceability: public-offer-client-link / Requirement: Public Offer Page
 */
import * as React from 'react';
import { useCallback, useState } from 'react';
import type {
  ClientOfferViewUnit,
  CarryForwardPrefill,
  OfferPeriodInquiryUnitSelection,
  PublicOfferViewResponse,
} from '@ooh/contracts';
import { isExpiredLinkError, parseApiError, publicOfferLink } from '@ooh/contracts';
import { SharedClientOfferContent } from '../client-offer/shared-client-offer-content';
import { PublicOfferReadOnlyContent } from './public-offer-read-only-content';
import { PublicOfferExpiredPage, PublicOfferInvalidPage } from './public-offer-error-pages';
import {
  ClientOfferPageLoading,
  ClientOfferPageError,
} from '../client-offer/client-offer-page-states';
import { usePublicApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { QueryBoundary } from '../../layouts/query-boundary';
import { usePublicAcceptVersion } from './use-public-accept';
import { usePublicSubmitPeriodInquiry } from './use-public-period-inquiry';
import {
  useClientOfferSelections,
  buildUnitDecisionsPayload,
  useWarnOnDroppedDecisions,
} from '../client-offer/hooks/use-client-offer-selections';
import { usePeriodInquirySummary } from '../client-offer/hooks/use-period-inquiry-summary';
import { useAcceptErrorHandler } from '../client-offer/hooks/use-accept-error-handler';
import { buildMediaUnitIdLookup } from '../client-offer/lib/build-media-unit-id-lookup';
import { buildEnrichedUnitById } from '../client-offer/lib/build-enriched-unit-lookup';
import { PublicOfferSuccessPage } from '../public-offer-success/public-offer-success-page';

// ─── Error resolver ───────────────────────────────────────────────────────────

function resolvePublicViewError(error: unknown): React.ReactElement {
  if (isExpiredLinkError(error)) return <PublicOfferExpiredPage />;
  const statusCode = parseApiError(error).statusCode;
  if (statusCode === 404) return <PublicOfferInvalidPage />;
  return <ClientOfferPageError />;
}

// ─── Public offer page ────────────────────────────────────────────────────────

interface PublicOfferPageProps {
  token: string;
  /** Opens the public media-unit detail route; the app owns the router. */
  onOpenUnitDetail: (mediaUnitId: number) => void;
}

function PublicOfferPageData({ token, onOpenUnitDetail }: PublicOfferPageProps) {
  const { data } = usePublicApiSuspenseQuery(publicOfferLink.publicClientView, { token });
  const acceptMutation = usePublicAcceptVersion(token);
  const periodInquiryMutation = usePublicSubmitPeriodInquiry(token);

  return (
    <ResolvedPublicOfferPage
      token={token}
      data={data}
      acceptMutation={acceptMutation}
      periodInquiryMutation={periodInquiryMutation}
      onOpenUnitDetail={onOpenUnitDetail}
    />
  );
}

export function PublicOfferPage(props: PublicOfferPageProps) {
  return (
    <QueryBoundary
      fallback={<ClientOfferPageLoading />}
      renderError={(error) => resolvePublicViewError(error)}
    >
      <PublicOfferPageData {...props} />
    </QueryBoundary>
  );
}

/**
 * Renders the confirmation-then-read-only pair for an accepted version — extracted so
 * `ResolvedPublicOfferPage` stays within the lint `complexity` budget. `justAccepted`
 * (the fresh-accept signal) shows the confirmation with its "way back" affordance;
 * `viewedReadOnly` (flipped by that affordance) or a plain reopen renders read-only.
 */
function renderAcceptedPublicOffer(
  data: PublicOfferViewResponse,
  justAccepted: boolean,
  viewedReadOnly: boolean,
  onViewOffer: () => void,
  onOpenUnitDetail: (mediaUnitId: number) => void,
): React.ReactElement {
  const { version, units, defaultDateFrom, defaultDateTo, recordedDecisions } = data;
  if (justAccepted && !viewedReadOnly) {
    return (
      <PublicOfferSuccessPage
        clientName={version.clientName ?? ''}
        offerTitle={version.offerTitle ?? ''}
        campaignFrom={defaultDateFrom}
        campaignTo={defaultDateTo}
        onViewOffer={onViewOffer}
      />
    );
  }
  return (
    <PublicOfferReadOnlyContent
      versionNumber={version.versionNumber}
      publishedAt={new Date(version.publishedAt)}
      acceptedAt={version.acceptedAt ?? null}
      clientName={version.clientName ?? ''}
      offerTitle={version.offerTitle ?? ''}
      defaultDateFrom={defaultDateFrom}
      defaultDateTo={defaultDateTo}
      units={units}
      recordedDecisions={recordedDecisions}
      onOpenUnitDetail={onOpenUnitDetail}
    />
  );
}

interface ResolvedPublicOfferPageProps {
  token: string;
  data: PublicOfferViewResponse;
  acceptMutation: ReturnType<typeof usePublicAcceptVersion>;
  periodInquiryMutation: ReturnType<typeof usePublicSubmitPeriodInquiry>;
  onOpenUnitDetail: (mediaUnitId: number) => void;
}

function ResolvedPublicOfferPage({
  token,
  data,
  acceptMutation,
  periodInquiryMutation,
  onOpenUnitDetail,
}: ResolvedPublicOfferPageProps) {
  const { version, diff, units, carryForwardPrefill, defaultDateFrom, defaultDateTo } = data;
  // Distinguishes "just accepted this session" (show the confirmation, with a way back
  // into the read-only view) from "reopening an already-accepted link" (skip straight to
  // read-only) — public-offer-link / Public Offer Page. `acceptMutation.isSuccess` is the
  // fresh-accept signal rather than `version.acceptedAt` alone, since the latter is true
  // in both cases; a fresh mount (reopen) always starts with a mutation in its initial,
  // not-yet-successful state. `viewedReadOnly` flips once the visitor uses the "way back"
  // affordance, without a full page reload. A withdrawn acceptance (rejectedAt also set)
  // falls through to the interactive PublicOfferContent below; rejectedAt itself is NOT
  // forwarded there — this product has no client-facing reject control, so rejectedAt on
  // this route only ever means a staff withdrawal, never a decision the visitor made
  // (client-offer-decision-flow bug fix).
  const [viewedReadOnly, setViewedReadOnly] = useState(false);
  const isAcceptedNow = version.acceptedAt != null && version.rejectedAt == null;
  const justAccepted = acceptMutation.isSuccess;

  if (isAcceptedNow || justAccepted) {
    return renderAcceptedPublicOffer(
      data,
      justAccepted,
      viewedReadOnly,
      () => setViewedReadOnly(true),
      onOpenUnitDetail,
    );
  }

  const validUntil = data.validUntil;
  const enrichedById = buildEnrichedUnitById(units);
  const mediaUnitIdByRowId = buildMediaUnitIdLookup(units);

  // clientName and offerTitle come from the snapshot root, populated by the API.
  const clientName = version.clientName ?? '';
  const offerTitle = version.offerTitle ?? '';

  return (
    <PublicOfferContent
      token={token}
      versionId={String(version.id)}
      enrichedById={enrichedById}
      mediaUnitIdByRowId={mediaUnitIdByRowId}
      validUntil={validUntil}
      versionNumber={version.versionNumber}
      publishedAt={new Date(version.publishedAt)}
      acceptedAt={version.acceptedAt ?? null}
      diff={diff}
      acceptMutation={acceptMutation}
      clientName={clientName}
      offerTitle={offerTitle}
      carryForwardPrefill={carryForwardPrefill ?? []}
      defaultDateFrom={defaultDateFrom}
      defaultDateTo={defaultDateTo}
      snapshotUnits={units}
      periodInquiryMutation={periodInquiryMutation}
      onOpenUnitDetail={onOpenUnitDetail}
    />
  );
}

interface PublicOfferContentProps {
  token: string;
  versionId: string;
  enrichedById: Map<number, ClientOfferViewUnit>;
  mediaUnitIdByRowId: Map<string, number>;
  validUntil: Date;
  versionNumber: number;
  publishedAt: Date;
  acceptedAt: Date | null;
  diff: React.ComponentProps<typeof SharedClientOfferContent>['diff'];
  periodInquiryMutation: ReturnType<typeof usePublicSubmitPeriodInquiry>;
  acceptMutation: ReturnType<typeof usePublicAcceptVersion>;
  clientName: string;
  offerTitle: string;
  carryForwardPrefill: CarryForwardPrefill;
  defaultDateFrom: Date | null;
  defaultDateTo: Date | null;
  snapshotUnits: React.ComponentProps<typeof SharedClientOfferContent>['snapshotUnits'];
  onOpenUnitDetail: (mediaUnitId: number) => void;
}

function PublicOfferContent({
  token,
  versionId,
  enrichedById,
  mediaUnitIdByRowId,
  validUntil,
  versionNumber,
  publishedAt,
  acceptedAt,
  diff,
  acceptMutation,
  clientName,
  offerTitle,
  carryForwardPrefill,
  defaultDateFrom,
  defaultDateTo,
  snapshotUnits,
  periodInquiryMutation,
  onOpenUnitDetail,
}: PublicOfferContentProps) {
  // No acceptDisabled overlay here — the per-unit Accept control is gated by
  // unit.canAccept (useClientOfferReviewUnits, live-recomputed per granularity
  // change). A prior acceptableUnitIds/applyAcceptGate overlay computed from a
  // client-item list here was frozen at INITIAL_GRANULARITY and only ever fed
  // "select all"/"clear all" (use-client-offer-selections.ts's nonSoldItems) —
  // removed rather than fixed (PR #985 review finding).
  const {
    decisions,
    selectedPeriods,
    granularity,
    periods,
    handleDecisionChange,
    handleGranularityChange,
    handlePeriodSelect,
    lockedRowIds,
    clearPersistedSelections,
  } = useClientOfferSelections({
    enrichedById,
    acceptedAt,
    validUntil,
    carryForwardPrefill,
    campaignFrom: defaultDateFrom,
    campaignTo: defaultDateTo,
    persistence: { scope: 'public', scopeId: token, versionId },
  });

  // CU-86cay88zz: warn instead of silently losing units whose decision was
  // dropped from the built unitDecisions payload. Shared hook (also used by
  // client-offer-page.tsx) so accept/reject and the SSO/public-link flows
  // cannot diverge in the warning trigger, message, or i18n key.
  const warnOnDroppedDecisions = useWarnOnDroppedDecisions();
  const handleAcceptError = useAcceptErrorHandler();

  const handleAccept = () => {
    const unitDecisions = buildUnitDecisionsPayload(
      decisions,
      selectedPeriods,
      mediaUnitIdByRowId,
      periods,
    );
    warnOnDroppedDecisions(decisions, unitDecisions);
    acceptMutation.mutate(
      { unitDecisions },
      { onSuccess: clearPersistedSelections, onError: handleAcceptError },
    );
  };

  // Shared period-inquiry submit action — same one client-offer-page.tsx uses, so the
  // two routes cannot drift on what gets submitted (qual-dry-violation).
  // Adapts to the anonymous public-link mutation shape (no offerId — the token scopes it).
  const submitInquiry = useCallback(
    (
      selection: OfferPeriodInquiryUnitSelection[],
      callbacks: { onSuccess: () => void; onError: (error: unknown) => void },
    ) => {
      periodInquiryMutation.mutate({ selection }, callbacks);
    },
    [periodInquiryMutation],
  );
  const { submitPeriodInquiry } = usePeriodInquirySummary({
    selectedPeriods,
    submit: submitInquiry,
  });

  return (
    <div className="h-screen overflow-y-auto" data-testid="public-offer-scroll-container">
      <SharedClientOfferContent
        versionNumber={versionNumber}
        publishedAt={publishedAt}
        acceptedAt={acceptedAt}
        diff={diff}
        onAccept={handleAccept}
        acceptMutation={acceptMutation}
        clientName={clientName}
        offerTitle={offerTitle}
        readOnly={false}
        validUntil={validUntil}
        decisions={decisions}
        onDecisionChange={handleDecisionChange}
        selectedPeriods={selectedPeriods}
        onPeriodSelect={handlePeriodSelect}
        snapshotUnits={snapshotUnits}
        enrichedUnitsById={enrichedById}
        showSupplierIdentity={false}
        campaignFrom={defaultDateFrom}
        campaignTo={defaultDateTo}
        accessMode="Link publiczny"
        onOpenUnitDetail={onOpenUnitDetail}
        lockedRowIds={lockedRowIds}
        periodInquiry={{
          onSubmit: submitPeriodInquiry,
          isPending: periodInquiryMutation.isPending,
        }}
        granularity={granularity}
        onGranularityChange={handleGranularityChange}
      />
    </div>
  );
}
